import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { getCategories } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { Mail, Lock, User, Phone, MapPin, ArrowRight, FileText, Hash, Building2 } from 'lucide-react';
import { logger } from '@/lib/logger';

export default function RegisterCompanyPage() {
  const navigate = useNavigate();
  const { registerCompany } = useAuth();
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [specializationsByFag, setSpecializationsByFag] = useState({});
  const [formData, setFormData] = useState({
    company_name: '',
    contact_person: '',
    email: '',
    password: '',
    phone: '',
    cvr_number: '',
    category: '',
    description: '',
    address: '',
    city: '',
    postal_code: '',
    specializations: [],
    newsletter_email: false,
    newsletter_sms: false
  });

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await getCategories();
        setCategories(response.data.categories);
        setSpecializationsByFag(response.data.specializations || {});
      } catch (error) {
        logger.error('Error fetching categories:', error);
      }
    };
    fetchCategories();
  }, []);

  const toggleSpecialization = (spec) => {
    setFormData((f) => ({
      ...f,
      specializations: f.specializations.includes(spec)
        ? f.specializations.filter((s) => s !== spec)
        : [...f.specializations, spec],
    }));
  };

  const validateCVR = (cvr) => {
    // Danish CVR numbers are 8 digits
    const cvrClean = cvr.replace(/\s/g, '');
    return /^\d{8}$/.test(cvrClean);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password.length < 6) {
      toast.error('Adgangskoden skal være mindst 6 tegn');
      return;
    }

    if (!formData.category) {
      toast.error('Vælg venligst en kategori');
      return;
    }

    if (!validateCVR(formData.cvr_number)) {
      toast.error('CVR-nummer skal være 8 cifre');
      return;
    }

    setLoading(true);
    
    try {
      await registerCompany(formData);
      toast.success('Virksomhed oprettet! Velkommen til Vibra');
      navigate('/company-dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Oprettelse fejlede. Prøv igen.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-50 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 mb-6">
            <img src="/logo.png" alt="Vibra" className="h-8 w-auto object-contain" />
            <span className="text-2xl font-bold bronze-text">Vibra</span>
          </Link>
          <h1 className="text-3xl font-bold tracking-tight">Opret virksomhed</h1>
          <p className="text-muted-foreground mt-2">
            Bliv synlig for tusindvis af potentielle kunder
          </p>
        </div>

        <Card className="border-border/50 shadow-lg" data-testid="register-company-card">
          <CardHeader>
            <CardTitle>Virksomhedsoplysninger</CardTitle>
            <CardDescription>
              Udfyld formularen for at oprette din virksomhedsprofil
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Company Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="company_name">Virksomhedsnavn *</Label>
                  <div className="relative">
                    <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                    <Input
                      id="company_name"
                      type="text"
                      placeholder="Firma A/S"
                      value={formData.company_name}
                      onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                      className="pl-10"
                      required
                      data-testid="company-name-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="cvr_number">CVR-nummer *</Label>
                  <div className="relative">
                    <Hash className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                    <Input
                      id="cvr_number"
                      type="text"
                      placeholder="12345678"
                      value={formData.cvr_number}
                      onChange={(e) => setFormData({ ...formData, cvr_number: e.target.value })}
                      className="pl-10"
                      maxLength={8}
                      required
                      data-testid="cvr-number-input"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">8-cifret CVR-nummer</p>
                </div>
              </div>

              {/* Contact Person */}
              <div className="space-y-2">
                <Label htmlFor="contact_person">Kontaktperson *</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    id="contact_person"
                    type="text"
                    placeholder="Fulde navn"
                    value={formData.contact_person}
                    onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
                    className="pl-10"
                    required
                    data-testid="contact-person-input"
                  />
                </div>
              </div>

              {/* Contact Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email *</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="firma@email.dk"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="pl-10"
                      required
                      data-testid="company-email-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone">Telefon *</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                    <Input
                      id="phone"
                      type="tel"
                      placeholder="+45 12 34 56 78"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="pl-10"
                      required
                      data-testid="company-phone-input"
                    />
                  </div>
                </div>
              </div>

              {/* Password */}
              <div className="space-y-2">
                <Label htmlFor="password">Adgangskode *</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="Mindst 6 tegn"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="pl-10"
                    required
                    data-testid="company-password-input"
                  />
                </div>
              </div>

              {/* Category */}
              <div className="space-y-2">
                <Label htmlFor="category">Håndværkertype (hovedfag) *</Label>
                <Select
                  value={formData.category}
                  onValueChange={(value) => setFormData({ ...formData, category: value, specializations: [] })}
                >
                  <SelectTrigger data-testid="category-select">
                    <SelectValue placeholder="Vælg kategori" />
                  </SelectTrigger>
                  <SelectContent className="max-h-60">
                    {categories.map((cat) => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Specializations (optional multi-select) */}
              {formData.category && (specializationsByFag[formData.category] || []).length > 0 && (
                <div className="space-y-2" data-testid="specializations-block">
                  <Label>Specialer (valgfrit – vælg dem der matcher)</Label>
                  <div className="flex flex-wrap gap-2">
                    {specializationsByFag[formData.category].map((spec) => {
                      const active = formData.specializations.includes(spec);
                      return (
                        <button
                          key={spec}
                          type="button"
                          onClick={() => toggleSpecialization(spec)}
                          className={`px-3 py-1.5 rounded-full text-sm border transition-all ${
                            active
                              ? 'bronze-shine border-transparent text-white shadow-md'
                              : 'bg-white border-stone-200 hover:border-[#c97f59] hover:text-[#c97f59]'
                          }`}
                          data-testid={`specialization-${spec}`}
                        >
                          {spec}
                        </button>
                      );
                    })}
                  </div>
                  <p className="text-xs text-muted-foreground">Kunder kan filtrere direkte på specialer</p>
                </div>
              )}

              {/* Description */}
              <div className="space-y-2">
                <Label htmlFor="description">Beskrivelse *</Label>
                <div className="relative">
                  <FileText className="absolute left-3 top-3 w-5 h-5 text-muted-foreground" />
                  <Textarea
                    id="description"
                    placeholder="Beskriv jeres services og ekspertise..."
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="pl-10 min-h-[100px]"
                    required
                    data-testid="company-description-input"
                  />
                </div>
              </div>

              {/* Location */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="address">Adresse *</Label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                    <Input
                      id="address"
                      type="text"
                      placeholder="Vejnavn 123"
                      value={formData.address}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      className="pl-10"
                      required
                      data-testid="company-address-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="city">By *</Label>
                  <Input
                    id="city"
                    type="text"
                    placeholder="København"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    required
                    data-testid="company-city-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="postal_code">Postnummer *</Label>
                  <Input
                    id="postal_code"
                    type="text"
                    inputMode="numeric"
                    placeholder="2100"
                    maxLength={4}
                    value={formData.postal_code}
                    onChange={(e) => setFormData({ ...formData, postal_code: e.target.value.replace(/\D/g, '').slice(0, 4) })}
                    required
                    data-testid="company-postal-input"
                  />
                  <p className="text-xs text-muted-foreground">Bruges til automatisk matching af kunder i dit område</p>
                </div>
              </div>

              {/* Newsletter Subscription */}
              <div className="space-y-3 py-2">
                <p className="text-sm font-medium">Nyhedsbrev</p>
                <div className="flex items-start space-x-3">
                  <Checkbox
                    id="newsletter-email"
                    checked={formData.newsletter_email}
                    onCheckedChange={(checked) => setFormData({ ...formData, newsletter_email: checked })}
                    data-testid="newsletter-email-checkbox"
                  />
                  <Label htmlFor="newsletter-email" className="text-sm cursor-pointer">
                    Modtag nyhedsbrev via e-mail
                  </Label>
                </div>
                <div className="flex items-start space-x-3">
                  <Checkbox
                    id="newsletter-sms"
                    checked={formData.newsletter_sms}
                    onCheckedChange={(checked) => setFormData({ ...formData, newsletter_sms: checked })}
                    data-testid="newsletter-sms-checkbox"
                  />
                  <Label htmlFor="newsletter-sms" className="text-sm cursor-pointer">
                    Modtag nyhedsbrev via SMS
                  </Label>
                </div>
              </div>

              <Button
                type="submit"
                className="w-full bg-primary hover:bg-primary/90"
                disabled={loading}
                data-testid="register-company-submit-button"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <span className="spinner" />
                    Opretter virksomhed...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    Opret virksomhed
                    <ArrowRight className="w-4 h-4" />
                  </span>
                )}
              </Button>
            </form>

            <div className="mt-6 text-center text-sm">
              <p className="text-muted-foreground">
                Har du allerede en konto?{' '}
                <Link to="/login" className="text-primary font-medium hover:underline" data-testid="login-link">
                  Log ind
                </Link>
              </p>
              <p className="text-muted-foreground mt-2">
                Leder du efter en håndværker?{' '}
                <Link to="/register" className="text-secondary font-medium hover:underline" data-testid="user-register-link">
                  Opret brugerkonto
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
